import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import HtmlMessage, { isHtmlContent, extractHtml } from './html-message';

/**
 * Turn bare URLs and e-mail addresses into markdown links.
 *
 * Agents routinely emit plain URLs, and without this they render as dead text.
 * Anything already inside a markdown link or a code span is left alone.
 */
const AUTOLINK_PATTERN =
  /(https?:\/\/[^\s<>()[\]]+|www\.[^\s<>()[\]]+|mailto:[^\s<>()[\]]+|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;
const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const CODE_SPAN_PATTERN = /(```[\s\S]*?```|`[^`\n]*`)/g;
// Existing markdown links/images must survive untouched. Lookbehind would be
// the direct way to spot them, but Safari only gained it recently, so the
// markdown syntax is captured by the splitter instead.
const MARKDOWN_LINK_PATTERN = /(!?\[[^\]]*\]\([^)]*\)|<[^>\s]+>)/g;

function autoLinkSegment(text) {
  return text.replace(AUTOLINK_PATTERN, match => {
    // Trailing sentence punctuation should stay outside the link.
    const trailing = match.match(/[.,;:!?]+$/);
    const url = trailing ? match.slice(0, -trailing[0].length) : match;
    const suffix = trailing ? trailing[0] : '';

    let href = url;
    if (EMAIL_PATTERN.test(href)) {
      href = `mailto:${href}`;
    } else if (!/^https?:\/\//i.test(href) && !/^mailto:/i.test(href)) {
      href = `https://${href}`;
    }
    return `[${url}](${href})${suffix}`;
  });
}

function autoLink(text) {
  if (!text || typeof text !== 'string') {
    return '';
  }
  // Split on code spans and existing links so URLs inside backticks or already
  // written as markdown are preserved verbatim.
  return text
    .split(CODE_SPAN_PATTERN)
    .map(segment => {
      if (segment.startsWith('`')) {
        return segment;
      }
      return segment
        .split(MARKDOWN_LINK_PATTERN)
        .map(part => (/^!?\[|^</.test(part) ? part : autoLinkSegment(part)))
        .join('');
    })
    .join('');
}

function toText(value) {
  if (typeof value === 'string') {
    return value;
  }
  if (value === null || value === undefined) {
    return '';
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

export default function TextMessage({ message, onInvokeAction, disabled }) {
  // A TEXT payload is normally a string, but replayed history can wrap it in
  // `{content}` / `{displayText}`. Rendering '' for those would silently blank
  // the turn, so unwrap one level before giving up.
  const content =
    typeof message === 'string'
      ? message
      : toText(message?.content ?? message?.displayText ?? message?.text);

  // A TEXT payload wrapped in <html>…</html> is rendered as (sanitized) HTML
  // instead of markdown. Everything else takes the markdown path unchanged.
  if (isHtmlContent(content)) {
    return (
      <HtmlMessage
        html={extractHtml(content)}
        onInvokeAction={onInvokeAction}
        disabled={disabled}
      />
    );
  }

  return (
    <ReactMarkdown
      className="bc-prose"
      remarkPlugins={[remarkGfm]}
      components={{
        // Link text comes from the markdown children, which the a11y lint rule
        // cannot see through the spread - hence passing them explicitly.
        a: ({ node, children, ...props }) => (
          <a
            {...props}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2"
          >
            {children}
          </a>
        ),
      }}
    >
      {autoLink(content)}
    </ReactMarkdown>
  );
}
